/* Cloud blob client — talks to lopettia-auth's /api/blobs.
 *
 * Generic per-user JSON storage with (product, kind, name) namespacing.
 * Designed to be product-agnostic so the same module copies cleanly into
 * La99 and future Lopettia apps — only the PRODUCT_SLUG constant changes.
 *
 * Used by AI Board for cloud-side session history:
 *   - kind='session' — full session snapshots
 *   - kind='preset'  — saved provider mixes (future)
 *
 * The Vite proxy maps /api/* → http://localhost:9100 so we keep first-party
 * cookies and no CORS dance.
 */

/**
 * Product slug used for blob namespacing.
 *
 * Lookup order:
 *   1. window.LP_PRODUCT — set this in the page <script> to override per-product
 *   2. 'aiboard' — default for this product
 *
 * La99 (or any sibling app) can drop this module unchanged and set
 * `window.LP_PRODUCT = 'la99'` before main.ts runs.
 */
const PRODUCT_SLUG: string =
  (typeof window !== 'undefined' && (window as any).LP_PRODUCT) || 'aiboard';

export interface BlobMeta {
  id: string;
  product: string;
  kind: string;
  name: string;
  size_bytes: number;
  is_pinned: boolean;
  created_at: string;
  updated_at: string;
}

export interface BlobFull extends BlobMeta {
  content: Record<string, unknown>;
}

export interface BlobListResponse {
  blobs: BlobMeta[];
  total: number;
  quota: {
    max_blobs: number;
    max_bytes: number;
    used_blobs: number;
    used_bytes: number;
  };
}

export class BlobApiError extends Error {
  status: number;
  detail: any;
  constructor(message: string, status: number, detail: any) {
    super(message);
    this.status = status;
    this.detail = detail;
  }
  get isQuotaExceeded(): boolean {
    return this.status === 413 || this.detail?.code === 'quota_exceeded';
  }
  get isUnauthorized(): boolean {
    return this.status === 401;
  }
}

async function blobsFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`/api/blobs${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...(init.headers || {}) },
    ...init,
  });
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  let body: any = null;
  if (text) { try { body = JSON.parse(text); } catch { body = text; } }
  if (!res.ok) {
    const detail = (body && typeof body === 'object') ? body.detail : body;
    const msg = typeof detail === 'object' && detail?.message
      ? detail.message
      : typeof detail === 'string'
      ? detail
      : `HTTP ${res.status}`;
    throw new BlobApiError(msg, res.status, detail);
  }
  return body as T;
}

/** List the caller's blobs in a namespace, pinned first then newest first. */
export function listBlobs(kind: string, opts: { limit?: number; offset?: number } = {}): Promise<BlobListResponse> {
  const params = new URLSearchParams({ product: PRODUCT_SLUG, kind });
  if (opts.limit  != null) params.set('limit',  String(opts.limit));
  if (opts.offset != null) params.set('offset', String(opts.offset));
  return blobsFetch<BlobListResponse>(`?${params.toString()}`);
}

/** Save a blob by name. If a blob with the same name already exists, it's
 *  overwritten in place (Save-style UX). */
export function saveBlob(
  kind: string,
  name: string,
  content: Record<string, unknown>,
  opts: { is_pinned?: boolean } = {},
): Promise<BlobFull> {
  return blobsFetch<BlobFull>('', {
    method: 'POST',
    body: JSON.stringify({
      product: PRODUCT_SLUG, kind, name, content, is_pinned: !!opts.is_pinned,
    }),
  });
}

export function getBlob(id: string): Promise<BlobFull> {
  return blobsFetch<BlobFull>(`/${encodeURIComponent(id)}`);
}

export function updateBlob(
  id: string,
  patch: { name?: string; content?: Record<string, unknown>; is_pinned?: boolean },
): Promise<BlobFull> {
  return blobsFetch<BlobFull>(`/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify(patch),
  });
}

export function deleteBlob(id: string): Promise<void> {
  return blobsFetch<void>(`/${encodeURIComponent(id)}`, { method: 'DELETE' });
}
